/**
 * 内容识别缩放（Seam Carving，M4-01 / DESIGN.md §2.11、§6.5）。
 *
 * 逐条找最小能量缝、移除或插入，直到达到目标尺寸。宽度用竖直缝，
 * 高度通过转置图像后复用同一套竖直缝逻辑（缝算法本身与方向无关）。
 *
 * 放大（插入缝）时反复插入同一批缝会产生可见的拉伸条纹——这是
 * Seam Carving 的已知局限，本实现不做论文里更复杂的"批量找 k 条缝再一次性
 * 插入"优化，接受这个质量上限（DESIGN.md 原话："质量上限低于 Photoshop…
 * 对小面积去除已经够用"，放大场景同理只保证能跑、不追求最佳质量）。
 */
import { applyEnergyMask, computeEnergy } from './energy';
import {
  findMinSeam,
  insertSeam,
  insertSeamChannel,
  removeSeam,
  removeSeamChannel,
  transposeRgba,
  transposeSingleChannel,
} from './seam';

export interface SeamCarveMasks {
  /** 保护区域（0–255）：值越高，缝越不可能穿过。 */
  readonly protect?: Uint8Array;
  /** 移除区域（0–255）：值越高，缝越优先穿过（用于删除该区域内容）。 */
  readonly remove?: Uint8Array;
}

export interface SeamCarveResult {
  readonly pixels: Uint8Array;
  readonly width: number;
  readonly height: number;
}

interface CarveWidthResult extends SeamCarveResult {
  readonly masks: SeamCarveMasks;
}

/** 沿宽度方向做 Seam Carving 到 targetWidth（不改变高度）。返回同步收缩/扩张后的蒙版。 */
function carveWidth(
  pixels: Uint8Array,
  width: number,
  height: number,
  targetWidth: number,
  masks: SeamCarveMasks,
  signal: AbortSignal | undefined,
): CarveWidthResult {
  let curPixels = pixels;
  let curWidth = width;
  let protectMask = masks.protect;
  let removeMask = masks.remove;

  while (curWidth !== targetWidth) {
    if (signal?.aborted === true) throw new DOMException('操作已中止', 'AbortError');
    const energy = computeEnergy(curPixels, curWidth, height);
    if (protectMask !== undefined) applyEnergyMask(energy, protectMask, 'protect');
    if (removeMask !== undefined) applyEnergyMask(energy, removeMask, 'remove');
    const seam = findMinSeam(energy, curWidth, height);

    if (curWidth > targetWidth) {
      curPixels = removeSeam(curPixels, curWidth, height, seam);
      if (protectMask !== undefined) protectMask = removeSeamChannel(protectMask, curWidth, height, seam);
      if (removeMask !== undefined) removeMask = removeSeamChannel(removeMask, curWidth, height, seam);
      curWidth -= 1;
    } else {
      curPixels = insertSeam(curPixels, curWidth, height, seam);
      if (protectMask !== undefined) protectMask = insertSeamChannel(protectMask, curWidth, height, seam);
      if (removeMask !== undefined) removeMask = insertSeamChannel(removeMask, curWidth, height, seam);
      curWidth += 1;
    }
  }
  return {
    pixels: curPixels,
    width: curWidth,
    height,
    masks: {
      ...(protectMask === undefined ? {} : { protect: protectMask }),
      ...(removeMask === undefined ? {} : { remove: removeMask }),
    },
  };
}

/**
 * 把图像缩放到目标尺寸，保留内容关键区域（高能量：边缘/纹理）不被压缩。
 * 先做宽度、再做高度（各自独立的一维问题，顺序不影响最终尺寸，
 * 只是中间过程的能量图不同——都是合理的实现选择，不存在"正确顺序"）。
 */
export function seamCarve(
  pixels: Uint8Array,
  width: number,
  height: number,
  targetWidth: number,
  targetHeight: number,
  masks: SeamCarveMasks = {},
  options: { signal?: AbortSignal } = {},
): SeamCarveResult {
  if (targetWidth < 1 || targetHeight < 1) {
    throw new RangeError(`目标尺寸必须为正，收到 ${targetWidth}×${targetHeight}`);
  }
  const afterWidth = carveWidth(pixels, width, height, targetWidth, masks, options.signal);

  if (afterWidth.height === targetHeight) return afterWidth;

  // 高度方向：转置后复用竖直缝逻辑，再转置回来。蒙版必须用**宽度阶段结束后**
  // 的版本（已经跟着宽度变化收缩/扩张过），不是原始输入的蒙版——否则尺寸对不上
  const transposedMasks: SeamCarveMasks = {
    ...(afterWidth.masks.protect === undefined
      ? {}
      : { protect: transposeSingleChannel(afterWidth.masks.protect, afterWidth.width, afterWidth.height) }),
    ...(afterWidth.masks.remove === undefined
      ? {}
      : { remove: transposeSingleChannel(afterWidth.masks.remove, afterWidth.width, afterWidth.height) }),
  };
  const transposed = transposeRgba(afterWidth.pixels, afterWidth.width, afterWidth.height);
  const afterHeight = carveWidth(
    transposed,
    afterWidth.height,
    afterWidth.width,
    targetHeight,
    transposedMasks,
    options.signal,
  );
  const finalPixels = transposeRgba(afterHeight.pixels, afterHeight.width, afterHeight.height);
  return { pixels: finalPixels, width: afterHeight.height, height: afterHeight.width };
}
